import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import FormLayoutGraphic from './FormLayoutGraphic';

const TemplateCard = ({ template, onUseTemplate }) => {
  return (
    <div className="group relative bg-card border border-border rounded-lg overflow-hidden transition-smooth hover:shadow-elevation-md">
      {/* Form layout graphic area */}
      <div className="relative aspect-[4/3] overflow-hidden bg-gray-50">
        <Link
          to={`/templates/${template?.slug}`}
          className="absolute inset-0 p-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset"
          aria-label={`View ${template?.title} template details`}
        >
          <FormLayoutGraphic formElements={template?.formElements} compact={true} templateId={template?.id} />
        </Link>

        {/* Category color accent bar at top */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 to-teal-400" />

        {template?.isFeatured && (
          <div className="absolute top-3 left-3 bg-accent text-accent-foreground px-2.5 py-1 rounded-md text-xs font-medium flex items-center gap-1.5 z-10">
            <Icon name="Star" size={12} />
            <span>Featured</span>
          </div>
        )}

        {template?.isNew && (
          <div className="absolute top-3 right-3 bg-success text-success-foreground px-2.5 py-1 rounded-md text-xs font-medium z-10">
            New
          </div>
        )}

        <div className="absolute inset-x-0 bottom-0 bg-background/95 backdrop-blur-sm flex items-center justify-center gap-3 p-3 translate-y-full group-hover:translate-y-0 group-focus-within:translate-y-0 transition-transform z-20">
          <Link
            to={`/templates/${template?.slug}`}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <Icon name="ExternalLink" size={15} />
            View Details
          </Link>
          <Button
            variant="default"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            onClick={() => onUseTemplate(template)}
          >
            Use Template
          </Button>
        </div>
      </div>

      <div className="p-4 md:p-5">
        <div className="flex items-start justify-between gap-3 mb-2">
          <h3 className="text-base md:text-lg font-heading font-semibold text-foreground line-clamp-1">
            <Link to={`/templates/${template?.slug}`} className="hover:text-primary transition-colors">
              {template?.title}
            </Link>
          </h3>
          <div className="flex items-center gap-1 text-muted-foreground flex-shrink-0">
            <Icon name="Users" size={14} />
            <span className="text-xs whitespace-nowrap">{template?.usageCount}</span>
          </div>
        </div>

        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
          {template?.description}
        </p>

        <Link
          to={`/templates/${template?.slug}`}
          className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline mb-3"
        >
          View template details
          <Icon name="ArrowRight" size={14} />
        </Link>

        <div className="flex items-center justify-between gap-3">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-primary/10 text-primary rounded-md text-xs font-medium">
            <Icon name={template?.categoryIcon} size={12} />
            {template?.category}
          </span>

          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <Icon name="FileText" size={12} />
              <span>{template?.fieldCount} fields</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateCard;